/**
 * Button — Primary (accent), Secondary (outline), Warn variants.
 * Height 36 default, radius r2, fs md, medium weight, icon gap 7.
 */
import React from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';

type Variant = 'primary' | 'secondary' | 'warn' | 'ghost';
type Props = {
  label: string;
  onPress?: () => void;
  variant?: Variant;
  height?: number;
  left?: React.ReactNode;
  right?: React.ReactNode;
  disabledLook?: boolean;
  // Truly inert: dims AND blocks the press. `title` carries the reason, so a
  // disabled control always says why (no dead controls).
  disabled?: boolean;
  title?: string;
};

export default function Button({ label, onPress, variant = 'primary', height = 36, left, right, disabledLook, disabled, title }: Props) {
  const v = VARIANTS[variant];
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      {...({ title, disabled } as object)}
      style={[
        styles.btn,
        { height, backgroundColor: v.bg, borderColor: v.border } as any,
        disabledLook ? ({ opacity: 1, backgroundColor: v.disabledBg || v.bg } as any) : null,
        disabled ? ({ opacity: 0.5, cursor: 'not-allowed' } as any) : null,
      ]}
    >
      {left}
      <Text style={[styles.text, { color: v.fg } as any]}>{label}</Text>
      {right}
    </Pressable>
  );
}

const VARIANTS: Record<Variant, { bg: string; border: string; fg: string; disabledBg?: string }> = {
  primary: { bg: colors.accent, border: colors.accent, fg: '#fff' },
  secondary: { bg: colors.card, border: colors.lineStrong, fg: colors.ink2 },
  warn: { bg: colors.warn, border: colors.warn, fg: '#fff' },
  ghost: { bg: colors.card, border: colors.line, fg: colors.ink },
};

const styles = StyleSheet.create({
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingHorizontal: 16,
    borderRadius: radii.r2,
    borderWidth: 1,
    cursor: 'pointer',
  } as any,
  text: { fontFamily: fontFamily.sans, fontSize: fontSize.md, fontWeight: fontWeight.medium },
});
